import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { useState } from "react";
import CandidateInformationForm from "./CandidateInformationForm";
import PersonalDetailsForm from "./PersonalDetailsForm";
import ExperienceForm from "./ExperienceForm";
import EducationForm from "./EducationForm";
import SubmitForm from "./SubmitForm";
import { setCurrentCandidateAI } from "reducers/candidate/currentCandidateAISlice";
import { useDispatch } from "react-redux";
import SocialMediaURL from "services/SocialMediaURL.service";

export default function RightTabsContainer({ setIsDialogClosed, candidateId }) {
    const [value, setValue] = useState("candidate-information");
    const [sections, setSections] = useState({
        candidateId: candidateId,
        personal: null,
        educations: null,
        experiences: null,
        skills: null,
    });
    const dispatch = useDispatch();
    const updateSection = (key, data) => {
        setSections((prev) => ({
            ...prev,
            [key]: data,
        }));
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const onSubmitCandidateInformation = (data) => {
        setValue("personal-details-preferences");

        const key = `currentCandidateAI-${candidateId}`;

        const existing = JSON.parse(localStorage.getItem(key) ?? "{}");

        Object.entries(data).forEach(([key, value]) => {
            if (!existing["candidateInformation"]) {
                existing["candidateInformation"] = {};
            }
            if (key === "linkedin") {
                existing["candidateInformation"]["linkedin"] = {
                    ...existing["candidateInformation"]["linkedin"],
                    data: SocialMediaURL.extractLinkedinId(value),
                };
            } else if (key === "dateOfBirth") {
                existing["candidateInformation"]["dateOfBirth"] = {
                    ...existing["candidateInformation"]["dateOfBirth"],
                    data: {
                        iso8601: value ? new Date(value).toISOString() : null,
                    },
                };
            } else {
                existing["candidateInformation"][key] = {
                    ...existing["candidateInformation"][key],
                    data: value ? value : null,
                };
            }
        });

        localStorage.setItem(
            key,
            JSON.stringify({
                ...existing,
            }),
        );

        dispatch(setCurrentCandidateAI(existing));
        updateSection("personal", data);
    };

    const onSubmitPersonalDetails = (data) => {
        const storageKey = `currentCandidateAI-${candidateId}`;
        const existing = JSON.parse(localStorage.getItem(storageKey) ?? "{}");

        const personalDetails = existing.personalDetails || {};

        Object.keys(data).forEach((sectionKey, index) => {
            Object.entries(data[sectionKey]).forEach(([key, value]) => {
                personalDetails[sectionKey][key] = {
                    ...personalDetails[sectionKey][key],
                    data: value ? value : null,
                };
            });
        });

        localStorage.setItem(storageKey, JSON.stringify(existing));
        dispatch(setCurrentCandidateAI(existing));
        updateSection("personalDetailsPreferences", data);
        setValue("experiences");
    };

    const onSubmitExperiences = (data) => {
        const key = `currentCandidateAI-${candidateId}`;

        const existing = JSON.parse(localStorage.getItem(key) ?? "{}");
        existing["experiences"]["data"] = data;

        localStorage.setItem(
            key,
            JSON.stringify({
                ...existing,
            }),
        );

        dispatch(setCurrentCandidateAI(existing));
        setValue("educations");
        updateSection("experiences", data);
    };

    const onSubmitEducations = (data) => {
        const key = `currentCandidateAI-${candidateId}`;

        const existing = JSON.parse(localStorage.getItem(key) ?? "{}");
        existing["educations"]["data"] = data;

        localStorage.setItem(
            key,
            JSON.stringify({
                ...existing,
            }),
        );

        dispatch(setCurrentCandidateAI(existing));
        setValue("submitsection");
        updateSection("educations", data);
    };

    const areAllSectionsCompleted = () => {
        return (
            sections.personal &&
            sections.personalDetailsPreferences &&
            sections.experiences &&
            sections.educations
        );
    };

    return (
        <Box sx={{ width: "100%", overflowY: "auto", height: "100%" }}>
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    width: "100%",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "white",
                    zIndex: 2,
                    overflowX: "auto",
                }}
            >
                <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="right tabs container"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ width: "100%" }}
                >
                    <Tab
                        label="Candidate Information"
                        value="candidate-information"
                    />
                    <Tab
                        sx={{
                            pointerEvents: sections.personal ? "auto" : "none",
                            opacity: sections.personal ? 1 : 0.5,
                        }}
                        label="Personal Details & Preferences"
                        value="personal-details-preferences"
                    />
                    <Tab
                        sx={{
                            pointerEvents: sections.personalDetailsPreferences
                                ? "auto"
                                : "none",
                            opacity: sections.personalDetailsPreferences
                                ? 1
                                : 0.5,
                        }}
                        label="Experiences"
                        value="experiences"
                    />
                    <Tab
                        sx={{
                            pointerEvents: sections.experiences
                                ? "auto"
                                : "none",
                            opacity: sections.experiences ? 1 : 0.5,
                        }}
                        label="Education"
                        value="educations"
                    />
                    <Tab
                        sx={{
                            pointerEvents: areAllSectionsCompleted()
                                ? "auto"
                                : "none",
                            opacity: areAllSectionsCompleted() ? 1 : 0.5,
                        }}
                        label="Submit"
                        value="submitsection"
                    />
                </Tabs>
            </Box>
            {value === "candidate-information" && (
                <Box sx={{ p: 3, paddingBottom: 0 }}>
                    <CandidateInformationForm
                        onSubmit={(data) => onSubmitCandidateInformation(data)}
                    />
                </Box>
            )}
            {value === "personal-details-preferences" && (
                <Box sx={{ p: 3, paddingBottom: 0 }}>
                    <PersonalDetailsForm
                        onSubmit={(data) => onSubmitPersonalDetails(data)}
                    />
                </Box>
            )}
            {value === "experiences" && (
                <Box sx={{ p: 3, paddingBottom: 0 }}>
                    <ExperienceForm
                        onSubmit={(data) => onSubmitExperiences(data)}
                    />
                </Box>
            )}
            {value === "educations" && (
                <Box sx={{ p: 3, paddingBottom: 0 }}>
                    <EducationForm
                        onSubmit={(data) => onSubmitEducations(data)}
                    />
                </Box>
            )}
            {value === "submitsection" && (
                <Box sx={{ p: 3, paddingBottom: 0 }}>
                    <SubmitForm
                        formData={sections}
                        setTabValue={setValue}
                        setIsDialogClosed={setIsDialogClosed}
                    />
                </Box>
            )}
        </Box>
    );
}
